// src/ui/pages/Emailing.js
import {
    listAllEmailsWithDetails, markEmailAsRead, createEmailRecord,
    sendEmailViaFunction, linkThreadToEvent, linkThreadToContact, linkThreadToVenue,
    listNeedsReviewEmails, resolveEmailReview, EMAIL_SENDING_ENABLED
} from "../../services/emailsService.js";
import { actions } from "../../app/state.js";
import { openEditEventModal, openCreateEventModal } from "../components/EventModal.js";
import { getUser, getRole, getCurrentTeamId } from "../../services/teamService.js";
import { mountRoute } from "../../app/router.js";
import { listEventsByContact, listAllEvents, listEventsByDate } from "../../services/eventsService.js";
import { searchVenues } from "../../services/venuesService.js";
import { searchContacts } from "../../services/contactsService.js";
import { normalizeSearch } from "../../utils/stringUtils.js";
import { CONTACT_TYPE_LABELS, CONTACT_TYPE_BADGE_CLASSES } from "../../domain/constants.js";



const state = {
    emails: [], // All email records
    threads: [], // Grouped by thread_id
    selectedThreadId: null,
    canReview: false, // admin o manager: quien puede resolver la revisión pendiente
    reviewEmails: [], // Emails con processing_status = 'needs_review'
    selectedReviewId: null,
};

// Tipos de evento reales en BD (events.type). No coincide con el enum "amplio"
// de src/domain/constants.js (EVENT_TYPES), así que se mapea aparte aquí.
const REVIEW_EVENT_TYPE_LABELS = { boda: "Boda", empresa: "Empresa", privado: "Privado" };

// Permite a otras paginas (p.ej. la busqueda global de "Hoy" en movil) pedir
// que se abra un hilo concreto en cuanto esta pagina termine de cargar.
let pendingThreadId = null;
export function openThreadOnLoad(threadId) {
    pendingThreadId = threadId;
}


function $(id) { return document.getElementById(id); }
function escapeHtml(str) {
    return String(str ?? "").replaceAll("&", "&amp;").replaceAll("<", "&lt;").replaceAll(">", "&gt;").replaceAll('"', "&quot;").replaceAll("'", "&#039;");
}

/** Strip HTML tags and convert to readable plain text */
function cleanEmailBody(html) {
    if (!html) return "";
    let text = html;
    // Decode common HTML entities first
    text = text.replace(/&nbsp;/gi, " ");
    text = text.replace(/&amp;/gi, "&");
    text = text.replace(/&lt;/gi, "<");
    text = text.replace(/&gt;/gi, ">");
    text = text.replace(/&quot;/gi, '"');
    text = text.replace(/&#039;/gi, "'");
    text = text.replace(/&#\d+;/gi, "");
    // Convert block elements to newlines
    text = text.replace(/<br\s*\/?>/gi, "\n");
    text = text.replace(/<\/div>/gi, "\n");
    text = text.replace(/<\/p>/gi, "\n\n");
    text = text.replace(/<\/tr>/gi, "\n");
    text = text.replace(/<\/li>/gi, "\n");
    text = text.replace(/<li[^>]*>/gi, "  • ");
    text = text.replace(/<\/td>/gi, "  ");
    text = text.replace(/<hr[^>]*>/gi, "\n---\n");
    // Remove all remaining HTML tags
    text = text.replace(/<[^>]+>/g, "");
    // Clean up excessive whitespace
    text = text.replace(/[ \t]+/g, " ");
    text = text.replace(/\n /g, "\n");
    text = text.replace(/ \n/g, "\n");
    text = text.replace(/\n{3,}/g, "\n\n");
    return text.trim();
}

/** Strip quoted/cited thread content from an email body */
function stripQuotedContent(body) {
    if (!body) return "";
    let text = body;

    // 1) Remove HTML blockquotes and gmail_quote divs entirely
    text = text.replace(/<blockquote[^>]*>[\s\S]*?<\/blockquote>/gi, "");
    text = text.replace(/<div\s+class="gmail_quote[^"]*"[^>]*>[\s\S]*$/gi, "");
    text = text.replace(/<div\s+class="gmail_attr"[^>]*>[\s\S]*$/gi, "");

    // 2) Cut at common quote headers (works on both HTML and plain text)
    const quotePatterns = [
        // Spanish Gmail: "El mié, 18 feb 2026 a las 12:44, ... escribió:"
        /\n?El\s+\w{3},\s+\d{1,2}\s+\w+\s+\d{4}[^]*?escribi[oó]:[\s\S]*$/i,
        // English Gmail: "On Wed, Feb 18, 2026 at 12:44 PM, ... wrote:"
        /\n?On\s+\w{3},\s+\w{3}\s+\d{1,2},\s+\d{4}[^]*?wrote:[\s\S]*$/i,
        // Generic: "On [date] [name] wrote:"
        /\n?On\s+.{10,60}\s+wrote:[\s\S]*$/i,
        // Generic Spanish: "El [date] [name] escribió:"
        /\n?El\s+.{10,60}\s+escribi[oó]:[\s\S]*$/i,
        // Forwarded message
        /\n?-{5,}\s*Forwarded message\s*-{5,}[\s\S]*$/i,
        /\n?-{5,}\s*Mensaje reenviado\s*-{5,}[\s\S]*$/i,
        // Original message (Outlook)
        /\n?-{3,}\s*Original Message\s*-{3,}[\s\S]*$/i,
        /\n?-{3,}\s*Mensaje original\s*-{3,}[\s\S]*$/i,
        // "From: ... Sent: ... To: ... Subject:" (Outlook header block)
        /\n?From:\s*.+\n\s*Sent:\s*.+\n[\s\S]*$/i,
        /\n?De:\s*.+\n\s*Enviado:\s*.+\n[\s\S]*$/i,
    ];

    for (const pattern of quotePatterns) {
        text = text.replace(pattern, "");
    }

    // 3) Remove plain-text quoted lines (lines starting with ">")
    //    Only if they appear at the end as a block
    const lines = text.split("\n");
    let cutIndex = lines.length;
    for (let i = lines.length - 1; i >= 0; i--) {
        const trimmed = lines[i].trim();
        if (trimmed.startsWith(">") || trimmed === "") {
            cutIndex = i;
        } else {
            break;
        }
    }
    // Only cut if we found quoted lines (not just empty lines at the end)
    const hasQuotedLines = lines.slice(cutIndex).some(l => l.trim().startsWith(">"));
    if (hasQuotedLines && cutIndex > 0) {
        text = lines.slice(0, cutIndex).join("\n");
    }

    return text.trim();
}

function formatDate(iso) {
    if (!iso) return "—";
    const d = new Date(iso);
    const now = new Date();
    if (d.toDateString() === now.toDateString()) {
        return d.toLocaleTimeString("es-ES", { hour: "2-digit", minute: "2-digit" });
    }
    return d.toLocaleDateString("es-ES", { day: "numeric", month: "short" });
}

async function loadEmails() {
    try {
        state.emails = await listAllEmailsWithDetails();
        groupThreads();
        renderList();

        if (pendingThreadId) {
            const threadId = pendingThreadId;
            pendingThreadId = null;
            if (state.threads.some(t => t.id === threadId)) {
                await selectEmail(threadId);
            }
        }
    } catch (err) {
        console.error(err);
        actions.addToast?.({ type: "error", message: "Error al cargar correos." });
    }
}

function groupThreads() {
    const threadMap = new Map();

    // Grouping
    state.emails.forEach(e => {
        const tid = e.thread_id || e.id; // Fallback to id if thread_id is missing
        if (!threadMap.has(tid)) {
            threadMap.set(tid, {
                id: tid,
                latest: e,
                messages: []
            });
        }
        threadMap.get(tid).messages.push(e);

        // Ensure 'latest' is the most recent
        if (new Date(e.created_at) > new Date(threadMap.get(tid).latest.created_at)) {
            threadMap.get(tid).latest = e;
        }
    });

    // Convert to array and sort by latest activity
    state.threads = Array.from(threadMap.values()).sort((a, b) =>
        new Date(b.latest.created_at) - new Date(a.latest.created_at)
    );
}


function renderList() {
    const listEl = $("email-list");
    if (!listEl) return;

    if (state.threads.length === 0) {
        listEl.innerHTML = '<div class="p-8 text-center text-slate-400 text-sm">No hay hilos de conversación.</div>';
        return;
    }

    const searchTerm = normalizeSearch($("email-search")?.value || "");
    const filterDir = $("filter-direction")?.value || "";
    const filterStatus = $("filter-status")?.value || "";

    const filtered = state.threads.filter(t => {
        const searchTermInLower = searchTerm.toLowerCase();
        
        // Search in ALL messages of the thread for more comprehensive results
        const matchesAnyMessage = t.messages.some(m => {
            const subject = normalizeSearch(m.subject || "");
            const contactName = normalizeSearch(m.contacts?.name || "");
            const contactEmail = normalizeSearch(m.contacts?.email || "");
            const eventName = normalizeSearch(m.events?.client_name_legacy || "");
            
            // Dates search
            const arrivalDateRaw = m.created_at ? m.created_at.split('T')[0] : "";
            const eventDateRaw = m.events?.date || "";
            
            // Format dates for search (e.g., 2026-02-18 and 18/02/2026)
            let arrivalDateES = "";
            if (m.created_at) {
                const dateObj = new Date(m.created_at);
                arrivalDateES = dateObj.toLocaleDateString("es-ES");
            }
            
            let eventDateES = "";
            if (m.events?.date) {
                const dateObj = new Date(m.events.date);
                eventDateES = dateObj.toLocaleDateString("es-ES");
            }

            return subject.includes(searchTerm) || 
                   contactName.includes(searchTerm) || 
                   contactEmail.includes(searchTerm) || 
                   eventName.includes(searchTerm) ||
                   arrivalDateRaw.includes(searchTerm) ||
                   arrivalDateES.includes(searchTerm) ||
                   eventDateRaw.includes(searchTerm) ||
                   eventDateES.includes(searchTerm) ||
                   normalizeSearch(cleanEmailBody(m.body || "")).includes(searchTerm);
        });

        const e = t.latest;
        // Direction Filter (checks latest)
        const matchesDir = filterDir === "" || e.direction === filterDir;

        // Status Filter (unread if ANY in thread is unread and inbound)
        const hasUnread = t.messages.some(m => m.direction === 'inbound' && !m.is_read);
        let matchesStatus = true;
        if (filterStatus === "unread") matchesStatus = hasUnread;
        if (filterStatus === "read") matchesStatus = !hasUnread;

        return matchesAnyMessage && matchesDir && matchesStatus;
    });

    const resultsCountHtml = `<div class="px-4 py-2 text-[10px] font-bold text-slate-400 uppercase tracking-widest bg-slate-50/50 border-b border-slate-100 flex justify-between items-center">
        <span>${filtered.length} hilos encontrados</span>
        ${searchTerm ? `<button onclick="document.getElementById('email-search').value=''; document.getElementById('email-search').dispatchEvent(new Event('input'));" class="text-blue-500 hover:text-blue-700">Limpiar</button>` : ''}
    </div>`;

    listEl.innerHTML = resultsCountHtml + filtered.map(t => {
        const e = t.latest;
        const isSelected = state.selectedThreadId === t.id;
        const hasUnread = t.messages.some(m => m.direction === 'inbound' && !m.is_read);
        const indicator = hasUnread ? '<div class="w-2 h-2 rounded-full bg-blue-500 animate-pulse"></div>' : '';
        const directionIcon = e.direction === 'inbound' ? '<i class="fa-solid fa-arrow-down-left text-emerald-500"></i>' : '<i class="fa-solid fa-arrow-up-right text-blue-500"></i>';

        const typeLabel = e.direction === 'inbound' ? 'Recibido' : 'Enviado';
        const typeColor = e.direction === 'inbound' ? 'emerald' : 'blue';

        const eventDateStr = e.events?.date ? `[${new Date(e.events.date).toLocaleDateString("es-ES", {day:'2-digit', month:'2-digit'})}] ` : '';

        return `
            <div class="p-4 rounded-2xl cursor-pointer transition-all border-2 ${isSelected ? 'bg-blue-50 border-blue-100 shadow-sm' : 'bg-transparent border-transparent hover:bg-slate-50'}"
                 data-id="${t.id}" onclick="window.EMAIL_SELECT('${t.id}')">
                <div class="flex items-center gap-2 mb-2">
                    <div class="flex-1 flex items-center gap-2 overflow-hidden">
                        ${directionIcon}
                        <span class="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-${typeColor}-100 text-${typeColor}-700 border border-${typeColor}-200">${typeLabel}</span>
                        <span class="text-xs font-bold text-slate-800 truncate">${escapeHtml(e.contacts?.name || 'Sistema')}</span>
                    </div>
                    ${indicator}
                    <span class="text-[10px] text-slate-400 whitespace-nowrap">${formatDate(e.created_at)}</span>
                </div>
                <div class="text-sm font-bold text-slate-900 truncate mb-1">${escapeHtml(e.subject || '(Sin Asunto)')}</div>
                <div class="text-[11px] text-slate-500 truncate leading-tight flex items-center gap-1">
                    <i class="fa-solid fa-calendar-day text-[9px] opacity-70"></i>
                    <span class="font-bold text-slate-600">${eventDateStr}</span>
                    <span>${escapeHtml(e.events?.client_name_legacy || 'Lead Directo')}</span>
                </div>
                <div class="mt-2 text-[10px] text-slate-400 font-medium">${t.messages.length} mensajes en el hilo</div>
            </div>
        `;
    }).join("");
}


const EMAIL_STAGE_LABELS = {
    lead: "Lead", contacted: "Contactado", meeting: "Reunión",
    proposal_sent: "Presupuesto", negotiation: "Negociación",
    accepted_pending_deposit: "Señal pendiente",
    confirmed: "Confirmado", done: "Realizado"
};

// Estados que "ocupan el día" a efectos de conflictos (igual que en EventModal.js / Events.js):
// una señal pendiente reserva la fecha igual que un confirmado/realizado.
const EMAIL_STAGES_BLOCKING_DATE = ["confirmed", "done", "accepted_pending_deposit"];

/**
 * Render date conflict banner in the email thread detail view.
 *
 * Plegado por defecto: una fecha muy solicitada puede tener diez o más
 * eventos, y la lista entera se comía el alto del panel hasta dejar el
 * mensaje del cliente sin sitio (que es lo que se viene a leer). El aviso
 * queda en una tira de una línea con el recuento, y el detalle se despliega
 * bajo demanda con su propio scroll.
 */
async function renderEmailDateConflicts(date, excludeEventId) {
    const banner = $("email-date-conflicts-banner");
    if (!banner) return;

    try {
        const events = await listEventsByDate(date, { excludeId: excludeEventId });
        if (!events.length) {
            banner.innerHTML = "";
            return;
        }

        const closed = events.filter(e => EMAIL_STAGES_BLOCKING_DATE.includes(e.stage));
        const pipeline = events.filter(e => !EMAIL_STAGES_BLOCKING_DATE.includes(e.stage));

        const dateStr = new Date(date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" });

        // El día ocupado manda sobre el pipeline: si hay algo cerrado, la tira
        // va en rojo aunque también haya presupuestos sueltos esa fecha.
        const blocked = closed.length > 0;
        const strip = blocked
            ? { bg: "#fef2f2", border: "#fca5a5", fg: "#991b1b", icon: "fa-triangle-exclamation", iconColor: "#dc2626" }
            : { bg: "#fefce8", border: "#fde68a", fg: "#854d0e", icon: "fa-circle-info", iconColor: "#ca8a04" };

        const resumen = [
            blocked ? `Día ocupado · ${closed.length} cerrado${closed.length > 1 ? "s" : ""}` : null,
            pipeline.length ? `${pipeline.length} en pipeline` : null,
        ].filter(Boolean).join(" · ");

        const linea = (ev, color) =>
            `<div style="font-size:12px;color:${color};padding:2px 0;">• <strong>${escapeHtml(ev.client_name_legacy || "Sin nombre")}</strong> (${ev.type || "—"}) — ${EMAIL_STAGE_LABELS[ev.stage] || ev.stage}</div>`;

        banner.innerHTML = `
            <div style="padding:0 24px;">
                <div style="background:${strip.bg};border:1px solid ${strip.border};border-radius:12px;margin-top:8px;overflow:hidden;">
                    <button type="button" id="email-conflicts-toggle" aria-expanded="false"
                        style="width:100%;display:flex;align-items:center;gap:8px;padding:8px 14px;background:none;border:none;cursor:pointer;text-align:left;color:${strip.fg};font-size:13px;">
                        <i class="fa-solid ${strip.icon}" style="color:${strip.iconColor};"></i>
                        <strong>${resumen}</strong>
                        <span style="opacity:.75;font-weight:500;">el ${dateStr}</span>
                        <i id="email-conflicts-chevron" class="fa-solid fa-chevron-down"
                            style="margin-left:auto;font-size:11px;opacity:.7;transition:transform .15s;"></i>
                    </button>
                    <div id="email-conflicts-detail" class="hidden custom-scrollbar"
                        style="max-height:28vh;overflow-y:auto;padding:0 14px 10px;">
                        ${closed.length ? `<div style="padding-top:2px;">${closed.map(ev => linea(ev, "#7f1d1d")).join("")}</div>` : ""}
                        ${pipeline.length ? `
                            <div style="margin-top:${closed.length ? "8px" : "2px"};padding-top:${closed.length ? "8px" : "0"};${closed.length ? `border-top:1px dashed ${strip.border};` : ""}">
                                <div style="font-size:11px;font-weight:700;color:#854d0e;padding-bottom:2px;">En pipeline</div>
                                ${pipeline.map(ev => linea(ev, "#713f12")).join("")}
                            </div>` : ""}
                    </div>
                </div>
            </div>
        `;

        const toggle = $("email-conflicts-toggle");
        const detail = $("email-conflicts-detail");
        const chevron = $("email-conflicts-chevron");
        if (toggle && detail) {
            toggle.onclick = () => {
                const abierto = !detail.classList.toggle("hidden");
                toggle.setAttribute("aria-expanded", String(abierto));
                if (chevron) chevron.style.transform = abierto ? "rotate(180deg)" : "";
            };
        }
    } catch (err) {
        console.error("Error fetching email date conflicts:", err);
        banner.innerHTML = "";
    }
}



async function selectEmail(threadId) {
    state.selectedThreadId = threadId;
    renderList();

    const thread = state.threads.find(x => x.id === threadId);
    if (!thread) return;

    const empty = $("email-detail-empty");
    const detail = $("email-detail-content");
    const inner = $("email-detail-inner");

    empty.classList.add("hidden");
    detail.classList.remove("hidden");
    document.body.classList.add("thread-open");

    // Track if any failed
    let anyFailed = false;

    // Marcar TODOS los del hilo como leídos
    const unreadMessages = thread.messages.filter(m => m.direction === 'inbound' && !m.is_read);
    for (const m of unreadMessages) {
        try {
            await markEmailAsRead(m.id);
            m.is_read = true;
        } catch (err) {
            console.error("No se pudo marcar como leído", m.id, err);
            anyFailed = true;
        }
    }

    if (anyFailed) {
        actions.addToast?.({ type: "error", message: "No se pudieron marcar algunos correos como leídos. Verifica permisos." });
    }
    renderList();

    const e = thread.latest;
    if (!inner) return;

    inner.innerHTML = `
        <div class="shrink-0 p-4 px-6 border-b border-slate-100 flex flex-col md:flex-row md:items-center justify-between sticky top-0 bg-white z-10 gap-4">
            <div>
                <h2 class="text-xl font-bold text-slate-900">${escapeHtml(e.subject)}</h2>
                <div class="flex items-center gap-2 mt-1">
                    <span class="text-xs text-slate-500">${escapeHtml(thread.messages[0].contacts?.email || '')}</span>
                    <span class="text-slate-300">•</span>
                    <span class="text-xs text-slate-500 italic">
                        ${e.event_id ? escapeHtml(e.events?.client_name_legacy || 'Evento') : 
                          e.venue_id ? `Lugar: ${escapeHtml(e.venues?.name)}` : 
                          (e.contact_id && !e.event_id) ? `Contacto: ${escapeHtml(e.contacts?.name)}` : 
                          'Transversal / Organizativo'}
                    </span>
                </div>
            </div>
            <div class="flex items-center gap-2">
                <button id="btn-link-event" class="px-3 py-1.5 text-xs font-bold text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-200">
                    <i class="fa-solid fa-link mr-1"></i>${(e.event_id || e.venue_id) ? 'Cambiar Vinculo' : 'Vincular hilo'}
                </button>
                ${e.event_id ? `<button id="btn-view-event" class="px-3 py-1.5 text-xs font-bold text-slate-600 hover:bg-slate-100 rounded-lg transition-colors border border-slate-200">Ver Evento</button>` : ''}
            </div>
        </div>

        <div id="email-date-conflicts-banner" class="shrink-0"></div>

        <!-- min-h-0: sin él, en un flex column este bloque no baja de la altura
             de su contenido, así que con el aviso de fecha ocupada arriba se
             salía del panel (overflow-hidden) y el hilo no se podía scrollear. -->
        <div class="flex-1 min-h-0 overflow-y-auto custom-scrollbar p-6 space-y-6 bg-slate-50/30">
            ${thread.messages.sort((a, b) => new Date(b.created_at) - new Date(a.created_at)).map(m => {
        const isOutbound = m.direction === 'outbound';
        return `
                    <div class="flex ${isOutbound ? 'justify-end' : 'justify-start'}">
                        <div class="max-w-[85%] ${isOutbound ? 'bg-blue-600 text-white shadow-blue-100' : 'bg-white text-slate-700 shadow-slate-100'} rounded-2xl shadow-md p-4 group relative">
                            <div class="flex items-center justify-between gap-4 mb-2">
                                <span class="text-[10px] font-bold uppercase ${isOutbound ? 'text-blue-100' : 'text-slate-400'}">${isOutbound ? 'Tú' : (m.contacts?.name || 'Cliente')}</span>
                                <span class="text-[10px] ${isOutbound ? 'text-blue-100' : 'text-slate-400'}">${formatDate(m.created_at)}</span>
                            </div>
                            <div class="text-sm leading-relaxed whitespace-pre-wrap">${escapeHtml(cleanEmailBody(stripQuotedContent(m.body)))}</div>
                        </div>
                    </div>
                `;
    }).join("")}
        </div>

        <div class="shrink-0 p-3 bg-white border-t border-slate-100">
            <div class="bg-slate-50 rounded-2xl border border-slate-200 shadow-sm p-2 px-3">
                <div class="flex items-center gap-3">
                    <div class="flex-1 flex flex-col">
                        <textarea id="reply-body" rows="1" 
                            class="w-full text-sm outline-none resize-none border-none p-1 bg-transparent focus:ring-0 min-h-[36px] max-h-32 py-2" 
                            placeholder="Escribe tu respuesta aquí..."></textarea>
                        <div id="reply-attachments-list" class="flex flex-wrap gap-2 mb-1"></div>
                    </div>
                    <div class="flex items-center gap-1.5">
                        <label title="Adjuntar archivos" class="flex items-center justify-center w-9 h-9 border border-slate-200 rounded-xl text-slate-500 hover:text-blue-600 hover:bg-white hover:border-blue-200 cursor-pointer transition-all">
                            <i class="fa-solid fa-paperclip text-sm"></i>
                            <input type="file" id="reply-attachments-input" class="hidden" multiple>
                        </label>
                        <button id="email-gmail-btn" title="Abrir en Gmail" class="w-9 h-9 border border-slate-200 text-slate-600 rounded-xl hover:bg-white hover:text-red-500 hover:border-red-200 transition-all flex items-center justify-center">
                            <i class="fa-brands fa-google"></i>
                        </button>
                        <button id="email-reply-btn" class="h-9 px-4 bg-blue-600 text-white text-xs font-bold rounded-xl hover:bg-blue-700 shadow-lg shadow-blue-200 transition-all flex items-center gap-2 ml-1">
                            <span>Enviar</span>
                            <i class="fa-solid fa-paper-plane text-[10px]"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    `;

    // Auto-resize textarea
    const replyBody = $("reply-body");
    if (replyBody) {
        replyBody.oninput = function () {
            this.style.height = 'auto';
            this.style.height = (this.scrollHeight) + 'px';
        };
    }

    // Event Link Logic
    const btnLink = $("btn-link-event");
    if (btnLink) {
        btnLink.onclick = () => openLinkEventModal(thread);
    }

    // Events
    if (e.event_id) {

        const btnView = $("btn-view-event");
        if (btnView) btnView.onclick = () => openEditEventModal(e.event_id, { onSaved: loadEmails });
    }

    // Date conflict alerts
    if (e.event_id && e.events?.date) {
        renderEmailDateConflicts(e.events.date, e.event_id);
    }

    let selectedFiles = [];

    const updateReplyAttachments = () => {
        const list = $("reply-attachments-list");
        if (!list) return;
        list.innerHTML = selectedFiles.map((file, index) => `
            <div class="flex items-center gap-2 px-2 py-1 bg-white border border-slate-200 rounded-lg text-[10px] text-slate-700 shadow-sm">
                <span class="truncate max-w-[100px] font-medium">${file.name}</span>
                <button class="text-slate-400 hover:text-red-500 remove-reply-att" data-index="${index}">
                    <i class="fa-solid fa-xmark"></i>
                </button>
            </div>
        `).join("");

        list.querySelectorAll('.remove-reply-att').forEach(btn => {
            btn.onclick = () => {
                selectedFiles.splice(parseInt(btn.dataset.index), 1);
                updateReplyAttachments();
            };
        });
    };

    const attInput = $("reply-attachments-input");
    if (attInput) {
        attInput.onchange = (e) => {
            const files = Array.from(e.target.files);
            selectedFiles = [...selectedFiles, ...files];
            updateReplyAttachments();
            e.target.value = '';
        };
    }

    const gmailBtn = $("email-gmail-btn");
    if (gmailBtn) {
        gmailBtn.onclick = () => {
            const recipient = e.contacts?.email || "";
            const subject = encodeURIComponent("RE: " + e.subject);
            const body = encodeURIComponent("\n\n---\nEn respuesta a su correo del " + formatDate(e.created_at));
            const gmailUrl = `https://mail.google.com/mail/?view=cm&fs=1&to=${recipient}&su=${subject}&body=${body}`;
            window.open(gmailUrl, '_blank');
        };
    }

    const replyBtn = $("email-reply-btn");
    if (!EMAIL_SENDING_ENABLED) {
        // Solo lectura: se responde desde Outlook. Se deja el botón de abrir en Gmail.
        replyBtn?.classList.add("hidden");
        $("reply-body")?.classList.add("hidden");
        $("reply-attachments-input")?.closest("label")?.classList.add("hidden");
    } else if (replyBtn) {
        replyBtn.onclick = async () => {
            const body = $("reply-body").value.trim();
            if (!body) return;

            replyBtn.disabled = true;
            replyBtn.innerHTML = '<i class="fa-solid fa-circle-notch fa-spin mr-2"></i>Enviando...';

            try {
                await sendEmailViaFunction({
                    to: e.contacts?.email,
                    subject: "RE: " + e.subject,
                    body: body,
                    attachments: selectedFiles
                });

                await createEmailRecord({
                    event_id: e.event_id,
                    contact_id: e.contact_id,
                    thread_id: thread.id, // PERSIST THREAD
                    subject: "RE: " + e.subject,
                    body: body,
                    direction: 'outbound',
                    status: 'sent'
                });

                actions.addToast?.({ type: "success", message: "Respuesta enviada." });
                await loadEmails();
                selectEmail(thread.id); // Reload view
            } catch (err) {
                console.error(err);
                actions.addToast?.({ type: "error", message: "Error al enviar respuesta." });
                replyBtn.disabled = false;
                replyBtn.innerHTML = '<span>Enviar</span><i class="fa-solid fa-paper-plane text-[10px]"></i>';
            }
        };
    }
}


// Global expose for onclick
window.EMAIL_SELECT = selectEmail;


// ═══════════════════════════════════════════════════════════
// TAB: REVISIÓN PENDIENTE (admin y manager)
// ═══════════════════════════════════════════════════════════
// Correos que process-email no pudo vincular a un evento con confianza
// (p.ej. una wedding planner o finca escribiendo desde la misma bandeja
// sobre varias bodas distintas). Cada fila trae en review_data todo lo que
// la IA extrajo + los eventos candidatos con su score, para no tener que
// volver a llamarla desde aquí.

async function loadReviewEmails() {
    if (!state.canReview) return;
    try {
        state.reviewEmails = await listNeedsReviewEmails();
        renderReviewList();
        updateReviewBadge();

        if (state.selectedReviewId) {
            if (state.reviewEmails.some(m => m.id === state.selectedReviewId)) {
                renderReviewDetail(state.selectedReviewId);
            } else {
                // Ya se resolvió (desde aquí u otra sesión) — volver al estado vacío.
                state.selectedReviewId = null;
                $("review-detail-empty")?.classList.remove("hidden");
                $("review-detail-content")?.classList.add("hidden");
            }
        }
    } catch (err) {
        console.error(err);
        actions.addToast?.({ type: "error", message: "Error al cargar correos pendientes de revisión." });
    }
}

function updateReviewBadge() {
    const badge = $("review-count-badge");
    if (!badge) return;
    const count = state.reviewEmails.length;
    if (count > 0) {
        badge.textContent = String(count);
        badge.classList.remove("hidden");
    } else {
        badge.classList.add("hidden");
    }
}

function renderReviewList() {
    const listEl = $("review-list");
    if (!listEl) return;

    if (state.reviewEmails.length === 0) {
        listEl.innerHTML = `<div class="p-8 text-center text-slate-400 text-sm">
            <i class="fa-solid fa-circle-check text-3xl text-emerald-300 mb-2 block"></i>
            No hay correos pendientes de revisión.
        </div>`;
        return;
    }

    listEl.innerHTML = state.reviewEmails.map(m => {
        const isSelected = state.selectedReviewId === m.id;
        const review = m.review_data || {};
        const proposedLabel = [
            review.event_type ? (REVIEW_EVENT_TYPE_LABELS[review.event_type] || review.event_type) : null,
            review.event_date ? new Date(review.event_date + "T00:00:00").toLocaleDateString("es-ES") : null,
        ].filter(Boolean).join(" · ");
        const candidateCount = (review.candidates || []).length;
        const displayName = m.contacts?.name || review.contact_name || "Sin nombre";

        return `
            <div class="p-4 rounded-2xl cursor-pointer transition-all border-2 ${isSelected ? 'bg-amber-50 border-amber-200 shadow-sm' : 'bg-transparent border-transparent hover:bg-slate-50'}"
                 data-id="${m.id}" onclick="window.REVIEW_SELECT('${m.id}')">
                <div class="flex items-center gap-2 mb-2">
                    <span class="px-2 py-0.5 rounded-lg text-[9px] font-black uppercase bg-amber-100 text-amber-700 border border-amber-200">
                        <i class="fa-solid fa-triangle-exclamation mr-1"></i>Revisar
                    </span>
                    <span class="text-xs font-bold text-slate-800 truncate flex-1">${escapeHtml(displayName)}</span>
                    <span class="text-[10px] text-slate-400 whitespace-nowrap">${formatDate(m.created_at)}</span>
                </div>
                <div class="text-sm font-bold text-slate-900 truncate mb-1">${escapeHtml(m.subject || '(Sin Asunto)')}</div>
                <div class="text-[11px] text-slate-500 truncate leading-tight">${escapeHtml(proposedLabel || 'Sin datos de evento')}</div>
                <div class="mt-2 text-[10px] text-amber-600 font-bold">${candidateCount} evento${candidateCount === 1 ? '' : 's'} posible${candidateCount === 1 ? '' : 's'}</div>
            </div>
        `;
    }).join("");
}

function selectReviewEmail(id) {
    state.selectedReviewId = id;
    renderReviewList();
    renderReviewDetail(id);
}
window.REVIEW_SELECT = selectReviewEmail;

function renderReviewDetail(id) {
    const email = state.reviewEmails.find(m => m.id === id);
    const empty = $("review-detail-empty");
    const detail = $("review-detail-content");
    const inner = $("review-detail-inner");
    if (!empty || !detail || !inner) return;

    if (!email) {
        empty.classList.remove("hidden");
        detail.classList.add("hidden");
        return;
    }
    empty.classList.add("hidden");
    detail.classList.remove("hidden");

    const review = email.review_data || {};
    const candidates = review.candidates || [];
    const typeLabel = CONTACT_TYPE_LABELS[review.contact_type] || review.contact_type || "—";
    const typeBadge = CONTACT_TYPE_BADGE_CLASSES[review.contact_type] || "bg-slate-50 text-slate-700 border-slate-100";
    const eventTypeLabel = review.event_type ? (REVIEW_EVENT_TYPE_LABELS[review.event_type] || review.event_type) : "—";
    const eventDateLabel = review.event_date
        ? new Date(review.event_date + "T00:00:00").toLocaleDateString("es-ES", { day: "numeric", month: "long", year: "numeric" })
        : "—";
    const canCreateNew = !!(review.event_type && review.event_date);

    inner.innerHTML = `
        <div class="p-6 border-b border-slate-100">
            <div class="flex items-start justify-between gap-4 mb-3">
                <div class="min-w-0">
                    <h2 class="text-xl font-bold text-slate-900 truncate">${escapeHtml(email.subject || "(Sin Asunto)")}</h2>
                    <div class="flex items-center gap-2 mt-1 flex-wrap">
                        <span class="text-xs text-slate-500">${escapeHtml(review.contact_email || email.contacts?.email || "")}</span>
                        <span class="text-slate-300">•</span>
                        <span class="text-xs text-slate-400">${formatDate(email.created_at)}</span>
                    </div>
                </div>
                <span class="px-2 py-1 rounded-lg text-[10px] font-black uppercase border ${typeBadge} whitespace-nowrap">${escapeHtml(typeLabel)}</span>
            </div>

            <div class="grid grid-cols-2 gap-3 mt-4">
                <div class="p-3 bg-slate-50 rounded-xl">
                    <div class="text-[10px] font-black uppercase text-slate-400 mb-1">Tipo de evento</div>
                    <div class="text-sm font-bold text-slate-800">${escapeHtml(eventTypeLabel)}</div>
                </div>
                <div class="p-3 bg-slate-50 rounded-xl">
                    <div class="text-[10px] font-black uppercase text-slate-400 mb-1">Fecha propuesta</div>
                    <div class="text-sm font-bold text-slate-800">${escapeHtml(eventDateLabel)}</div>
                </div>
                ${review.couple_names ? `
                <div class="p-3 bg-pink-50 rounded-xl col-span-2">
                    <div class="text-[10px] font-black uppercase text-pink-400 mb-1">Pareja mencionada por el remitente</div>
                    <div class="text-sm font-bold text-pink-800">${escapeHtml(review.couple_names)}</div>
                </div>` : ''}
                ${review.event_location ? `
                <div class="p-3 bg-slate-50 rounded-xl col-span-2">
                    <div class="text-[10px] font-black uppercase text-slate-400 mb-1">Ubicación</div>
                    <div class="text-sm font-bold text-slate-800">${escapeHtml(review.event_location)}</div>
                </div>` : ''}
                ${review.ai_summary ? `
                <div class="p-3 bg-blue-50 rounded-xl col-span-2">
                    <div class="text-[10px] font-black uppercase text-blue-400 mb-1">Resumen IA</div>
                    <div class="text-sm text-blue-900">${escapeHtml(review.ai_summary)}</div>
                </div>` : ''}
            </div>
        </div>

        <div class="p-6 border-b border-slate-100">
            <div class="text-[10px] font-black uppercase text-slate-400 mb-2">Correo original</div>
            <div class="text-sm text-slate-700 whitespace-pre-wrap bg-slate-50 rounded-xl p-4 max-h-40 overflow-y-auto">${escapeHtml(cleanEmailBody(stripQuotedContent(review.body || email.body || "")))}</div>
        </div>

        <div class="p-6 border-b border-slate-100">
            <div class="text-[10px] font-black uppercase text-slate-400 mb-3">Eventos posibles (${candidates.length})</div>
            ${candidates.length === 0 ? `<p class="text-xs text-slate-400">No hay eventos que se parezcan a este — probablemente sea uno nuevo.</p>` : `
            <div class="space-y-2">
                ${candidates.map(c => {
                    const reasonLabel = c.reason === "same_sender"
                        ? { text: "Mismo contacto", cls: "bg-blue-50 text-blue-600 border-blue-100" }
                        : { text: "Misma fecha, contacto distinto", cls: "bg-amber-50 text-amber-600 border-amber-100" };
                    return `
                    <div class="flex items-center justify-between gap-3 p-3 border border-slate-200 rounded-xl hover:bg-slate-50 transition-colors">
                        <div class="min-w-0">
                            <div class="text-sm font-bold text-slate-800 truncate">${escapeHtml(c.client_name_legacy || "Sin nombre")}</div>
                            <div class="text-[11px] text-slate-400 flex items-center gap-1.5 flex-wrap mt-0.5">
                                <span>${c.date ? new Date(c.date + "T00:00:00").toLocaleDateString("es-ES") : "—"} · Coincidencia: ${c.score} pts</span>
                                <span class="px-1.5 py-0.5 rounded-md text-[9px] font-bold border ${reasonLabel.cls}">${reasonLabel.text}</span>
                            </div>
                        </div>
                        <div class="flex items-center gap-1 flex-shrink-0">
                            <button class="review-view-event-btn w-8 h-8 flex items-center justify-center text-slate-500 hover:bg-slate-100 rounded-lg transition-colors" data-ev-id="${c.event_id}" title="Ver evento">
                                <i class="fa-solid fa-eye text-xs"></i>
                            </button>
                            <button class="review-assign-btn px-3 py-1.5 text-[10px] font-bold text-white bg-slate-900 hover:bg-slate-800 rounded-lg transition-colors" data-ev-id="${c.event_id}">
                                Vincular aquí
                            </button>
                        </div>
                    </div>
                `;
                }).join("")}
            </div>`}
        </div>

        <div class="p-6 border-b border-slate-100">
            <div class="text-[10px] font-black uppercase text-slate-400 mb-2">Buscar otro evento</div>
            <div class="relative mb-2">
                <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                <input type="text" id="review-event-search" placeholder="Buscar por nombre de evento..."
                    class="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 bg-slate-50 transition-all">
            </div>
            <div id="review-event-search-results" class="space-y-1 max-h-40 overflow-y-auto"></div>
        </div>

        <div class="p-6 flex flex-col sm:flex-row gap-2">
            <button id="review-create-new-btn" ${canCreateNew ? '' : 'disabled title="Faltan tipo de evento o fecha para crear uno nuevo"'}
                class="flex-1 px-4 py-2.5 bg-blue-600 text-white text-sm font-bold rounded-xl hover:bg-blue-700 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                <i class="fa-solid fa-plus mr-1"></i> Crear evento nuevo con estos datos
            </button>
            <button id="review-dismiss-btn"
                class="px-4 py-2.5 text-slate-500 text-sm font-bold rounded-xl border border-slate-200 hover:bg-slate-50 transition-all">
                Descartar (sin evento)
            </button>
        </div>
    `;

    inner.querySelectorAll(".review-assign-btn").forEach(btn => {
        btn.onclick = () => handleResolveReview(id, "assign", btn.dataset.evId);
    });
    inner.querySelectorAll(".review-view-event-btn").forEach(btn => {
        btn.onclick = () => openEditEventModal(btn.dataset.evId, { onSaved: () => loadReviewEmails() });
    });

    const createBtn = $("review-create-new-btn");
    if (createBtn) {
        createBtn.onclick = () => {
            if (!canCreateNew) return;
            handleResolveReview(id, "create_new");
        };
    }
    $("review-dismiss-btn").onclick = () => {
        if (!confirm("¿Descartar este correo sin vincularlo a ningún evento?")) return;
        handleResolveReview(id, "dismiss");
    };

    // Búsqueda manual de evento (por si ninguno de los candidatos es correcto)
    const searchInput = $("review-event-search");
    const resultsEl = $("review-event-search-results");
    if (searchInput && resultsEl) {
        searchInput.oninput = async () => {
            const q = normalizeSearch(searchInput.value);
            if (q.length < 2) { resultsEl.innerHTML = ""; return; }
            if (!state.allEventsCache) {
                state.allEventsCache = await listAllEvents({ orderBy: "date", ascending: false });
            }
            const matches = state.allEventsCache
                .filter(ev => normalizeSearch(ev.client_name_legacy || "").includes(q))
                .slice(0, 15);
            resultsEl.innerHTML = matches.map(ev => `
                <button class="review-search-assign-btn w-full text-left p-2.5 rounded-lg border border-slate-100 hover:bg-slate-50 transition-colors text-xs" data-ev-id="${ev.id}">
                    <span class="font-bold text-slate-800">${escapeHtml(ev.client_name_legacy || "Sin nombre")}</span>
                    <span class="text-slate-400"> — ${ev.date || "—"}</span>
                </button>
            `).join("") || `<p class="text-xs text-slate-400 px-1">Sin resultados.</p>`;
            resultsEl.querySelectorAll(".review-search-assign-btn").forEach(btn => {
                btn.onclick = () => handleResolveReview(id, "assign", btn.dataset.evId);
            });
        };
    }
}

async function handleResolveReview(emailId, action, eventId) {
    try {
        await resolveEmailReview({ emailId, action, eventId });
        actions.addToast?.({
            type: "success",
            message: action === "dismiss" ? "Correo descartado." : "Evento vinculado correctamente."
        });
        state.selectedReviewId = null;
        $("review-detail-empty")?.classList.remove("hidden");
        $("review-detail-content")?.classList.add("hidden");
        await loadReviewEmails();
        // El correo resuelto ya tiene event_id — puede aparecer en la bandeja normal.
        await loadEmails();
    } catch (err) {
        console.error(err);
        actions.addToast?.({ type: "error", message: err.message || "Error al resolver la revisión." });
    }
}


export async function init() {
    // RBAC: admin y manager pueden acceder.
    try {
        const user = await getUser();
        const role = await getRole(user.id);
        if (role !== 'manager' && role !== 'admin') {
            actions.addToast?.({ type: "error", message: "No tienes permiso para acceder a esta sección." });
            mountRoute("dashboard");
            return;
        }
        state.canReview = role === 'admin' || role === 'manager';
    } catch (err) {
        console.error(err);
        mountRoute("dashboard");
        return;
    }

    // Reset Search UI implicitly by clearing input since state derives from DOM mostly here
    const searchInput = $("email-search");
    if (searchInput) searchInput.value = "";
    const dirSelect = $("filter-direction");
    if (dirSelect) dirSelect.value = "";
    const statusSelect = $("filter-status");
    if (statusSelect) statusSelect.value = "";

    await loadEmails();

    // UI events
    $("email-refresh")?.addEventListener("click", () => {
        if (getActiveTab() === "review") {
            state.selectedReviewId = null;
            loadReviewEmails();
        } else {
            state.selectedThreadId = null;
            loadEmails();
        }
    });
    $("email-search")?.addEventListener("input", () => renderList());
    $("filter-direction")?.addEventListener("change", () => renderList());
    $("filter-status")?.addEventListener("change", () => renderList());

    $("email-back-btn")?.addEventListener("click", () => {
        document.body.classList.remove("thread-open");
        state.selectedThreadId = null;
        renderList();
    });

    // "Revisión pendiente" es para admin y manager — a viewer ni le llega el tab
    // (tampoco entra a Mails), y el servidor lo vuelve a comprobar al resolver.
    if (state.canReview) {
        $("email-tab-btn-review")?.classList.remove("hidden");
        await loadReviewEmails();
    }
    initTabs();
}

function getActiveTab() {
    return $("email-tab-content-review")?.classList.contains("hidden") ? "inbox" : "review";
}

function initTabs() {
    const btnInbox = $("email-tab-btn-inbox");
    const btnReview = $("email-tab-btn-review");
    const contentInbox = $("email-tab-content-inbox");
    const contentReview = $("email-tab-content-review");
    if (!btnInbox || !btnReview || !contentInbox || !contentReview) return;

    const activate = (tab) => {
        const toInbox = tab === "inbox";
        btnInbox.classList.toggle("border-slate-900", toInbox);
        btnInbox.classList.toggle("text-slate-900", toInbox);
        btnInbox.classList.toggle("border-transparent", !toInbox);
        btnInbox.classList.toggle("text-slate-500", !toInbox);

        btnReview.classList.toggle("border-slate-900", !toInbox);
        btnReview.classList.toggle("text-slate-900", !toInbox);
        btnReview.classList.toggle("border-transparent", toInbox);
        btnReview.classList.toggle("text-slate-500", toInbox);

        contentInbox.classList.toggle("hidden", !toInbox);
        contentReview.classList.toggle("hidden", toInbox);

        if (!toInbox) loadReviewEmails();
    };

    btnInbox.onclick = () => activate("inbox");
    btnReview.onclick = () => activate("review");
}



async function openLinkEventModal(thread) {
    const hasContact = !!thread.latest.contact_id;

    try {
        // Suggest events from this contact (only if contact exists)
        const suggested = hasContact ? await listEventsByContact(thread.latest.contact_id) : [];
        const allEvents = await listAllEvents({ orderBy: "date", ascending: false });

        // Simple Modal overlay
        const modal = document.createElement("div");
        modal.className = "modal-backdrop animate-fadeIn";
        modal.innerHTML = `
            <div class="modal-dialog">
                <div class="modal-header flex items-center justify-between">
                    <div>
                        <h3 class="text-lg font-bold text-slate-900">Vincular hilo</h3>
                        <p class="text-xs text-slate-500 mt-1">Selecciona el evento, lugar o contacto correspondiente.</p>
                    </div>
                    <button id="modal-create-event" class="px-3 py-2 bg-slate-900 text-white text-[10px] font-black uppercase tracking-wider rounded-xl hover:bg-slate-800 transition-all flex items-center gap-2">
                        <i class="fa-solid fa-plus"></i>
                        Nuevo Evento
                    </button>
                </div>
                
                ${!hasContact ? `<div class="px-6 pt-4">
                    <div class="p-3 bg-amber-50 border border-amber-200 rounded-xl text-xs text-amber-700 flex items-center gap-2">
                        <i class="fa-solid fa-triangle-exclamation"></i>
                        <span>Este hilo no tiene contacto asociado. Puedes vincular a un evento existente o crear uno nuevo.</span>
                    </div>
                </div>` : ''}

                <div class="px-6 pt-4">
                    <div class="relative">
                        <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 text-xs"></i>
                        <input type="text" id="modal-event-search" placeholder="Buscar evento, lugar o contacto..." 
                            class="w-full pl-9 pr-3 py-2 text-sm border border-slate-200 rounded-xl focus:outline-none focus:border-blue-500 bg-slate-50 transition-all">
                    </div>
                </div>

                <div class="modal-body space-y-4" id="modal-events-list">
                    <!-- Dynamic rendering -->
                    <!-- Suggested section -->
                    <div id="modal-suggested-section"></div>
                    <!-- Venues section -->
                    <div id="modal-venues-section"></div>
                    <!-- Contacts section -->
                    <div id="modal-contacts-section"></div>
                    <!-- All Events section -->
                    <div id="modal-all-events-section"></div>
                </div>
                <div class="modal-footer">
                    <button id="modal-cancel-link" class="px-4 py-2 text-xs font-bold text-slate-500 hover:bg-slate-200 rounded-xl">Cancelar</button>
                </div>
            </div>
        `;

        document.body.appendChild(modal);

        // Attach event listeners for buttons
        // (Removed undefined call)

        // Load Initial State
        const suggestSection = modal.querySelector("#modal-suggested-section");
        const allEvSection = modal.querySelector("#modal-all-events-section");
        suggestSection.innerHTML = renderSuggestedList(suggested);
        allEvSection.innerHTML = renderAllEventsList(allEvents.slice(0, 10));
        attachClickListeners(modal, thread, loadEmails, selectEmail);

        // Search Logic
        const searchInput = modal.querySelector("#modal-event-search");
        searchInput.oninput = async (e) => {
            const query = normalizeSearch(e.target.value);
            const teamId = await getCurrentTeamId();

            if (query.length < 2) {
                suggestSection.innerHTML = renderSuggestedList(suggested);
                allEvSection.innerHTML = renderAllEventsList(allEvents.slice(0, 10));
                modal.querySelector("#modal-venues-section").innerHTML = "";
                modal.querySelector("#modal-contacts-section").innerHTML = "";
            } else {
                const [vResults, cResults] = await Promise.all([
                    searchVenues(teamId, query),
                    searchContacts(teamId, query)
                ]);

                const filteredEvents = allEvents.filter(ev =>
                    normalizeSearch(ev.client_name_legacy || ev.name || "").includes(query)
                );

                suggestSection.innerHTML = "";
                allEvSection.innerHTML = renderAllEventsList(filteredEvents);
                modal.querySelector("#modal-venues-section").innerHTML = renderVenuesList(vResults);
                modal.querySelector("#modal-contacts-section").innerHTML = renderContactsList(cResults);
            }
            attachClickListeners(modal, thread, loadEmails, selectEmail);
        };
        
        // ... rest of event creation logic same as before ...
        modal.querySelector("#modal-create-event").onclick = () => {
            document.body.removeChild(modal);
            openCreateEventModal({
                initialData: {
                    contactId: thread.latest.contact_id,
                    client_name_legacy: thread.latest.subject || "Nuevo Evento desde Correo"
                },
                onSaved: async (created) => {
                    if (!created?.id) return;
                    try {
                        await linkThreadToEvent(thread.id, created.id);
                        actions.addToast?.({ type: "success", message: "Evento creado y hilo vinculado correctamente." });
                        await loadEmails();
                        selectEmail(thread.id);
                    } catch (e) {
                        console.error(e);
                        actions.addToast?.({ type: "error", message: "Error al vincular el nuevo evento." });
                    }
                }
            });
        };

        modal.querySelector("#modal-cancel-link").onclick = () => document.body.removeChild(modal);

        function renderSuggestedList(list) {
            if (!list.length) return "";
            return `<div><span class="text-[10px] font-black uppercase text-blue-600 tracking-wider">Sugeridos (Del contacto)</span><div class="mt-2 space-y-1">` + 
                list.map(ev => `<button class="w-full text-left p-3 rounded-xl border border-blue-100 bg-blue-50/50 hover:bg-blue-50 transition-colors text-sm font-bold text-blue-900 mb-1 event-select-btn" data-ev-id="${ev.id}">${escapeHtml(ev.client_name_legacy || ev.name)}<div class="text-[10px] font-normal text-blue-600">${ev.date || '—'}</div></button>`).join("") + `</div></div>`;
        }

        function renderAllEventsList(list) {
            if (!list.length) return "";
            return `<div><span class="text-[10px] font-black uppercase text-slate-400 tracking-wider">Eventos</span><div class="mt-2 space-y-1">` + 
                list.map(ev => `<button class="w-full text-left p-3 rounded-xl border border-slate-100 hover:bg-slate-50 transition-colors text-sm font-medium text-slate-700 mb-1 event-select-btn" data-ev-id="${ev.id}">${escapeHtml(ev.client_name_legacy || ev.name)}<div class="text-[10px] text-slate-400">${ev.date || '—'}</div></button>`).join("") + `</div></div>`;
        }

        function renderVenuesList(list) {
            if (!list.length) return "";
            return `<div><span class="text-[10px] font-black uppercase text-emerald-600 tracking-wider">Lugares</span><div class="mt-2 space-y-1">` + 
                list.map(v => `<button class="w-full text-left p-3 rounded-xl border border-emerald-100 hover:bg-emerald-50 transition-colors text-sm font-medium text-emerald-700 mb-1 venue-select-btn" data-v-id="${v.id}"><i class="fa-solid fa-location-dot mr-2"></i>${escapeHtml(v.name)}</button>`).join("") + `</div></div>`;
        }

        function renderContactsList(list) {
            if (!list.length) return "";
            return `<div><span class="text-[10px] font-black uppercase text-amber-600 tracking-wider">Contactos / Otros</span><div class="mt-2 space-y-1">` + 
                list.map(c => `<button class="w-full text-left p-3 rounded-xl border border-amber-100 hover:bg-amber-50 transition-colors text-sm font-medium text-amber-700 mb-1 contact-select-btn" data-c-id="${c.id}"><i class="fa-solid fa-user mr-2"></i>${escapeHtml(c.name)} (${c.type})</button>`).join("") + `</div></div>`;
        }

        function attachClickListeners(modal, thread, loadEmails, selectEmail) {
            modal.querySelectorAll(".event-select-btn").forEach(btn => {
                btn.onclick = async () => {
                    await linkThreadToEvent(thread.id, btn.dataset.evId);
                    actions.addToast?.({ type: "success", message: "Vínculo actualizado." });
                    document.body.removeChild(modal);
                    await loadEmails();
                    selectEmail(thread.id);
                };
            });
            modal.querySelectorAll(".venue-select-btn").forEach(btn => {
                btn.onclick = async () => {
                    await linkThreadToVenue(thread.id, btn.dataset.vId);
                    actions.addToast?.({ type: "success", message: "Vinculado a lugar." });
                    document.body.removeChild(modal);
                    await loadEmails();
                    selectEmail(thread.id);
                };
            });
            modal.querySelectorAll(".contact-select-btn").forEach(btn => {
                btn.onclick = async () => {
                    await linkThreadToContact(thread.id, btn.dataset.cId);
                    actions.addToast?.({ type: "success", message: "Vinculado a contacto." });
                    document.body.removeChild(modal);
                    await loadEmails();
                    selectEmail(thread.id);
                };
            });
        }

    } catch (err) {
        console.error(err);
        actions.addToast?.({ type: "error", message: "Error al cargar datos." });
    }
}


export default init;
